/**
 * useOrchestrationChatbotRouting Hook
 *
 * Routes chatbot messages with orchestration intent through the AI generation
 * pipeline (useOrchestrationAIGenerate) instead of the regular chat flow.
 *
 * When the user is on the Orchestration page and sends a message that matches
 * orchestration keywords (e.g., "add step", "chain variables", "reorder"),
 * the message is intercepted and processed through the orchestration AI service.
 *
 * The hook manages:
 * - Intent detection via detectOrchestrationIntent
 * - Mode selection (populate if 0 steps, edit if ≥1 steps)
 * - Generation via useOrchestrationAIGenerate
 * - Preview actions displayed in chat as a special message
 * - Confirm/Cancel actions for applying changes
 *
 * Requirements: 11.1, 11.2, 11.3, 11.5, 11.6
 */

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { detectOrchestrationIntent } from '../services/orchestrationIntentDetector';
import { useOrchestrationAIGenerate } from '../../orchestration/hooks/useOrchestrationAIGenerate';
import { selectSelectedOrchestration } from '../../../store/slices/orchestrationSlice';
import { getPageData } from './usePageDataCollector';

/**
 * Build orchestration-specific LivePageData context for the AI generation request.
 * Mirrors the context the modal flow uses, ensuring the chatbot has access to
 * the same full orchestration context.
 *
 * @param {Object} orchestration - The selected orchestration from Redux
 * @returns {Object} LivePageData context object
 */
function buildOrchestrationContext(orchestration) {
  const pageData = getPageData();

  const context = {
    page: 'Orchestration',
  };

  // Include orchestration state
  if (orchestration) {
    context.orchestration = {
      orchestration_id: orchestration.orchestration_id,
      name: orchestration.orchestration_name || orchestration.name || null,
      total_steps: orchestration.steps?.length || 0,
      steps: orchestration.steps || [],
    };
  }

  // Include project data if available from page data store
  if (pageData.project) {
    context.project = pageData.project;
  }

  return context;
}

/**
 * Hook that routes chatbot messages with orchestration intent through the
 * AI generation pipeline.
 *
 * @param {Object} chatContext - The chat context from useChatContext (has `page` field)
 * @param {Function} onStepListRefresh - Callback to refresh orchestration steps after apply
 * @returns {{
 *   shouldRouteToOrchestration: (message: string) => boolean,
 *   handleOrchestrationMessage: (message: string) => Promise<void>,
 *   orchestrationAI: Object,
 *   isOrchestrationRouted: boolean,
 * }}
 */
export function useOrchestrationChatbotRouting(chatContext, onStepListRefresh) {
  const orchestration = useSelector(selectSelectedOrchestration);

  // Refresh callback: refresh step list (triggers re-render of orchestration page)
  const orchestrationAI = useOrchestrationAIGenerate(orchestration, onStepListRefresh);

  /**
   * Check if a message should be routed to the orchestration AI pipeline.
   */
  const shouldRouteToOrchestration = useCallback(
    (message) => {
      const currentPage = chatContext?.page || '';
      return detectOrchestrationIntent(message, currentPage);
    },
    [chatContext],
  );

  /**
   * Handle a chatbot message by routing it through orchestration AI generation.
   * Determines mode based on step count and invokes the generate function.
   * @param {string} message - The user's message
   * @param {Array} [mentions] - Optional entity mentions from @mention autocomplete
   */
  const handleOrchestrationMessage = useCallback(
    async (message, mentions) => {
      const stepCount = orchestration?.steps?.length || 0;
      const mode = stepCount === 0 ? 'orchestration_populate' : 'orchestration_edit';

      // Build the full context for the AI service
      const context = buildOrchestrationContext(orchestration);

      // Use provided mentions from @mention autocomplete, or empty array
      const mentionsList = Array.isArray(mentions) && mentions.length > 0 ? mentions : [];

      await orchestrationAI.generate(message, mentionsList, mode, context);
    },
    [orchestration, orchestrationAI],
  );

  /**
   * Whether the chatbot is currently in an orchestration-routed state
   * (generating, previewing, or applying orchestration actions).
   */
  const isOrchestrationRouted = useMemo(
    () =>
      orchestrationAI.isGenerating ||
      orchestrationAI.isApplying ||
      orchestrationAI.previewActions !== null,
    [orchestrationAI.isGenerating, orchestrationAI.isApplying, orchestrationAI.previewActions],
  );

  return {
    shouldRouteToOrchestration,
    handleOrchestrationMessage,
    orchestrationAI,
    isOrchestrationRouted,
  };
}

export default useOrchestrationChatbotRouting;
